import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  MetaAdsIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import { WarningInfo } from '@/components/ui/warning-info'
import { cn } from '@/lib/utils'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import {
  isPublishablePlatform,
  type PlatformType,
  type PublishablePlatformType,
} from '@backend/lib/db/schema/platform'

interface Props {
  selectedPlatform: PlatformType
  onSelectedPlatformChange: (platform: PlatformType) => void
  activePlatforms: PlatformType[]
  onActivePlatformsChange: (platforms: PlatformType[]) => void
  /** Optional per-platform compatibility map. When set, platforms in `activePlatforms`
   *  that aren't compatible render a warning bubble. */
  compatibility?: Record<PlatformType, { compatible: boolean }>
  /** Wrapper class override — preview popup uses overflow-x-auto + extra padding
   *  so the warning bubble has room. */
  className?: string
  /** When true, hide generation-only targets (e.g. Meta Ads) — used by the
   *  publish/preview context where only connectable platforms make sense. */
  publishableOnly?: boolean
}

export interface SelectedAccount {
  // Accounts can only be selected for publishable platforms.
  platform: PublishablePlatformType
  accountId: string
  displayName: string
  imageUrl?: string
}

const PLATFORM_DISPLAY_ORDER: PlatformType[] = [
  'linkedin',
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'meta-ads',
]

export function WorkspaceHeaderSocialAccounts({
  selectedPlatform,
  onSelectedPlatformChange,
  activePlatforms,
  onActivePlatformsChange,
  compatibility,
  className,
  publishableOnly,
}: Props) {
  const platforms = publishableOnly
    ? PLATFORM_DISPLAY_ORDER.filter(isPublishablePlatform)
    : PLATFORM_DISPLAY_ORDER

  const handleTogglePlatform = (platform: PlatformType, event: React.MouseEvent) => {
    event.stopPropagation()

    if (activePlatforms.includes(platform)) {
      const newPlatforms = activePlatforms.filter((p) => p !== platform)
      if (newPlatforms.length === 0) return

      onActivePlatformsChange(newPlatforms)

      if (selectedPlatform === platform) {
        onSelectedPlatformChange(newPlatforms[0]!)
      }
    } else {
      onActivePlatformsChange([...activePlatforms, platform])
    }
  }

  const handlePlatformClick = (platform: PlatformType) => {
    if (!activePlatforms.includes(platform)) {
      onActivePlatformsChange([...activePlatforms, platform])
      onSelectedPlatformChange(platform)
    } else {
      onSelectedPlatformChange(platform)
    }
  }

  return (
    <div className={cn('flex w-full items-center justify-start gap-4', className)}>
      {platforms.map((platform) => {
        const isEnabled = activePlatforms.includes(platform)
        const hasWarning = !!compatibility && isEnabled && !compatibility[platform].compatible

        return (
          <SocialPlatformIcon
            key={platform}
            platform={platform}
            isCaptionActive={selectedPlatform === platform && isEnabled}
            isEnabled={isEnabled}
            activePlatformsCount={activePlatforms.length}
            hasWarning={hasWarning}
            onClick={() => handlePlatformClick(platform)}
            onToggle={(e) => handleTogglePlatform(platform, e)}
          />
        )
      })}
    </div>
  )
}

interface SocialPlatformIconProps {
  platform: PlatformType
  isCaptionActive: boolean
  isEnabled: boolean
  activePlatformsCount: number
  hasWarning: boolean
  onClick: () => void
  onToggle: (e: React.MouseEvent) => void
}

function SocialPlatformIcon({
  platform,
  isCaptionActive,
  isEnabled,
  activePlatformsCount,
  hasWarning,
  onClick,
  onToggle,
}: SocialPlatformIconProps) {
  const [hovered, setHovered] = useState(false)

  // `name` is the button's only accessible name — the button is icon-only, so
  // without it screen readers and automated drivers see five unlabelled circles.
  const platformConfig: Record<
    PlatformType,
    { name: string; colors: string; icon: React.ReactNode }
  > = {
    instagram: {
      name: 'Instagram',
      colors: 'bg-pink-500 hover:bg-pink-600',
      icon: <InstagramIcon width={18} height={18} color="#FFFFFF" />,
    },
    linkedin: {
      name: 'LinkedIn',
      colors: 'bg-blue-500 hover:bg-blue-600',
      icon: <LinkedinIcon width={18} height={18} color="#FFFFFF" />,
    },
    tiktok: {
      name: 'TikTok',
      colors: 'bg-black hover:bg-gray-800',
      icon: <TikTokIcon width={18} height={18} color="#FFFFFF" />,
    },
    youtube: {
      name: 'YouTube',
      colors: 'bg-red-600 hover:bg-red-700',
      icon: <YoutubeIcon width={18} height={18} color="#FFFFFF" />,
    },
    facebook: {
      name: 'Facebook',
      colors: 'bg-[#1877F2] hover:bg-[#0E61C7]',
      icon: <FacebookIcon width={18} height={18} color="#FFFFFF" />,
    },
    'meta-ads': {
      name: 'Meta Ads',
      colors: 'bg-[#0866FF] hover:bg-[#0656D6]',
      icon: <MetaAdsIcon width={18} height={18} color="#FFFFFF" />,
    },
  }

  const config = platformConfig[platform]
  const buttonEnabledClasses = config.colors

  return (
    <button
      aria-label={config.name}
      aria-pressed={isCaptionActive && isEnabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition-all',
        isEnabled
          ? buttonEnabledClasses
          : 'bg-gray-400 opacity-50 hover:bg-gray-400 hover:opacity-100',
        // Halo around the active platform — mirrors the analytics filter so
        // every platform selector across the app reads the same way.
        isCaptionActive && isEnabled && 'ring-primary ring-offset-background ring-2 ring-offset-2',
      )}>
      {config.icon}
      {!isEnabled && (
        <div className="border-card absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-2 bg-gray-400">
          <Plus className="h-3 w-3 text-white" />
        </div>
      )}
      {/* Warning bubble — only rendered when a compatibility map was supplied
          (i.e. preview-popup context) and this platform is enabled but
          incompatible with the current media. */}
      <div className="absolute -bottom-1 -right-1">
        <WarningInfo hasWarning={hasWarning} />
      </div>
      {hovered && isEnabled && activePlatformsCount > 1 && (
        <div
          onClick={onToggle}
          className="border-card absolute -right-1 -top-1 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border-2 bg-red-500 hover:bg-red-600">
          <X className="h-3 w-3 text-white" />
        </div>
      )}
    </button>
  )
}
